// Keep this file free of other src imports — privileged checkout requires it
// from Node on the server (extensionless ESM imports crash `yarn start`).
export const START_NOW_JOIN_SEARCH = 'enter=1';
export const START_NOW_JOIN_HASH = 'session-join';

export const START_NOW_EXISTING_ORDER_CODE = 'start_now_existing_order';

export const START_NOW_PENDING_PAYMENT_TRANSITION = 'transition/request-payment';

export const START_NOW_PAID_TRANSITIONS = [
  'transition/confirm-payment',
  'transition/accept',
  'transition/operator-accept',
];

export const START_NOW_RECOVER_TRANSITIONS = [
  START_NOW_PENDING_PAYMENT_TRANSITION,
  ...START_NOW_PAID_TRANSITIONS,
];

/**
 * Listing id on a denormalised tx or a raw JSON:API resource.
 *
 * @param {Object} tx
 * @returns {string|null}
 */
export const listingIdFromTransaction = tx =>
  tx?.listing?.id?.uuid || tx?.relationships?.listing?.data?.id?.uuid || null;

/**
 * True when last transition means the Start now card payment already completed.
 *
 * @param {string} lastTransition
 * @returns {boolean}
 */
export const isStartNowPaidTransition = lastTransition =>
  START_NOW_PAID_TRANSITIONS.includes(lastTransition);

/**
 * True when checkout actually finished a marketplace payment
 * (confirm-payment or Accept). Pending request-payment is not success.
 *
 * @param {Object|string} txOrTransition
 * @returns {boolean}
 */
export const isPaidMarketplaceCheckout = txOrTransition => {
  const lastTransition =
    typeof txOrTransition === 'string'
      ? txOrTransition
      : txOrTransition?.attributes?.lastTransition;
  return isStartNowPaidTransition(lastTransition);
};

/**
 * True when last transition is an unpaid Start now request.
 *
 * @param {string} lastTransition
 * @returns {boolean}
 */
export const isStartNowPendingPaymentTransition = lastTransition =>
  lastTransition === START_NOW_PENDING_PAYMENT_TRANSITION;

/**
 * Skip speculative initiate when this transaction already has a quote.
 *
 * @param {Object} tx
 * @returns {boolean}
 */
export const shouldSkipCheckoutSpeculate = tx =>
  Array.isArray(tx?.attributes?.lineItems) && tx.attributes.lineItems.length > 0;

/**
 * Start now session is still open unless the booking end is in the past.
 *
 * @param {Object} tx
 * @param {number} [nowMs]
 * @returns {boolean}
 */
export const isStartNowSessionOpen = (tx, nowMs = Date.now()) => {
  const booking = tx?.booking?.attributes || {};
  const endMs = Date.parse(booking.displayEnd || booking.end);
  if (Number.isFinite(endMs) && nowMs > endMs) {
    return false;
  }
  return true;
};

/**
 * Pick the talker's recoverable Start now order for this listing/format.
 * Prefers an in-flight card payment over an already-paid Join-ready session.
 *
 * @param {Array<Object>} transactions
 * @param {Object} [filters]
 * @param {string|{uuid: string}} [filters.listingId]
 * @param {string} [filters.sessionFormat]
 * @param {number} [filters.nowMs]
 * @returns {{ kind: 'pending_payment'|'join_ready', transaction: Object }|null}
 */
export const pickRecoverableStartNowOrder = (
  transactions,
  { listingId, sessionFormat, nowMs = Date.now() } = {}
) => {
  const listingUuid = listingId?.uuid || listingId || null;
  const matches = (transactions || []).filter(tx => {
    if (tx?.attributes?.protectedData?.startNow !== true) {
      return false;
    }
    const txListingId = listingIdFromTransaction(tx);
    if (listingUuid && txListingId && txListingId !== listingUuid) {
      return false;
    }
    const txFormat = tx?.attributes?.protectedData?.sessionFormat;
    if (sessionFormat && txFormat && txFormat !== sessionFormat) {
      return false;
    }
    // An unfinished card payment still holds the slot after the clock
    // printed on the booking. Treat that as recoverable so Pay does not
    // look like “time just been booked” with no way out.
    if (isStartNowPendingPaymentTransition(tx?.attributes?.lastTransition)) {
      return true;
    }
    return isStartNowSessionOpen(tx, nowMs);
  });

  const pending = matches.find(tx =>
    isStartNowPendingPaymentTransition(tx?.attributes?.lastTransition)
  );
  if (pending) {
    return { kind: 'pending_payment', transaction: pending };
  }

  const paid = matches.find(tx => isStartNowPaidTransition(tx?.attributes?.lastTransition));
  if (paid) {
    return { kind: 'join_ready', transaction: paid };
  }
  return null;
};

/**
 * Append Join deep-link when this checkout is Start now.
 *
 * @param {string} path
 * @param {boolean} [startNow]
 * @returns {string}
 */
export const withStartNowJoin = (path, startNow = false) =>
  startNow ? `${path}?${START_NOW_JOIN_SEARCH}#${START_NOW_JOIN_HASH}` : path;

/**
 * Structured 409 from initiate-privileged when the session is already paid.
 *
 * @param {Object} error
 * @returns {boolean}
 */
export const isStartNowExistingOrderError = error =>
  error?.data?.code === START_NOW_EXISTING_ORDER_CODE ||
  error?.code === START_NOW_EXISTING_ORDER_CODE;

/**
 * Transaction id on a start_now_existing_order error.
 *
 * @param {Object} error
 * @returns {string|null}
 */
export const startNowExistingOrderId = error =>
  error?.data?.transactionId || error?.transactionId || null;

/**
 * Which checkout errors to hide when the conflict is the talker's own in-flight Start now.
 *
 * @param {Object} params
 * @returns {{ suppressBookingTimeConflict: boolean, hideGenericBreakdownError: boolean, showUnfinishedPayment: boolean, showJoinExisting: boolean }}
 */
export const checkoutErrorDisplay = ({
  startNow = false,
  unfinishedPayment = false,
  hasExistingBreakdown = false,
  recoveredKind = null,
  existingPendingTx = false,
} = {}) => {
  const ownInFlight =
    !!unfinishedPayment ||
    recoveredKind === 'pending_payment' ||
    recoveredKind === 'join_ready' ||
    !!existingPendingTx;
  const startNowOwn = !!startNow && ownInFlight;
  // A slot conflict is not “your Inbox holds this”. Only hide Confirm when
  // this talker already has an in-flight or paid Start now.
  return {
    suppressBookingTimeConflict: startNowOwn,
    hideGenericBreakdownError: !!(hasExistingBreakdown || startNowOwn),
    showUnfinishedPayment: !!unfinishedPayment || recoveredKind === 'pending_payment',
    showJoinExisting: recoveredKind === 'join_ready',
    showInboxEscape: startNowOwn,
  };
};
